import React from 'react'
import clsx from 'clsx'

import ExpansionArrow from '../../../atoms/ExpansionArrow'
import PrimaryAssigneeSection from './Details/PrimaryAssigneeSection'
import AssignedToSection from './Details/AssignedToSection'
import DueDateSection from './Details/DueDateSection'
import CategorySection from './Details/CategorySection'
import ClientSection from './Details/ClientSection'
import TypeSection from './Details/TypeSection'
import WatchersSection from './Details/WatchersSection'
import PrioritySection from './Details/PrioritySection'
import StatusSection from './Details/StatusSection'
import TeamSection from './Details/TeamSection'
import OwnerSection from './Details/OwnerSection'

import useAccess, { PERMS } from '../../../../hooks/useAccess'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import useStyles from './styles/TaskDetails'
import { useSupabase } from '@SonarCX/providers/SupabaseProvider'

const TaskDetails = ({
  values,
  agents,
  types,
  teams,
  categories,
  statuses,
  currentUser,
  priorities,
  handleChange,
  searchUser,
  sendValues,
  setModifiedTask = () => { },
  canComplete,
  canEdit,
  setNewUserName,
  newUserName,
  canSearchUser,
  canAddClient,
  showExpand,
  setExpanded,
  expanded,
  isSubTask
}) => {
  const { getUsers, allUsers, loadUser } = useUserAPI()
  const hasPermission = useAccess()
  const { taskCategoryRules: { applyTaskCategoryRules } } = useSupabase()

  const [noPrimary, setNoPrimary] = React.useState([])
  const [clientObj, setClientObj] = React.useState('')
  const [leadObj, setLeadObj] = React.useState(null)
  const [ownerObj, setOwnerObj] = React.useState(null)

  const classes = useStyles({ canEdit })

  // handler for removing field on clicking "x"
  const removeUser = (prop, user) => {
    switch (prop) {
      case 'primary':
        handleChange(prop)({ target: { value: user.id, action: 'clear' } })
        break
      case 'watchers':
        handleChange(prop)({ target: { value: user.id, action: 'clear' } })
        break
      default:
    }
  }

  // Creating a new list of agents to pass into options if selected
  React.useEffect(() => {
    const list = [currentUser, ...agents].filter(Boolean)

    if (values.primary?.length > 0) {
      values.primary.forEach(id => {
        const index = list.findIndex(o => o.id === id)
        if (index >= 0) {
          list.splice(index, 1)
        }
      })
    }

    return setNoPrimary(list)
  }, [agents, values.primary, currentUser])

  // Set client
  React.useEffect(() => {
    const id = values.client

    if (!id) {
      setClientObj('')
      return
    }

    if (hasPermission([PERMS.ContactsOwn, PERMS.ContactsAudit])) {
      const guests = getUsers(true)
      const guestName = guests?.[id]
      setClientObj(guestName)
      return
    }

    const user = allUsers[id]
    if (user) {
      setClientObj(user)
    } else {
      loadUser(id)
    }
  }, [values, getUsers, loadUser, allUsers, hasPermission])

  // Set lead
  React.useEffect(() => {
    const id = values.primary_assignee_id

    if (!id) {
      setLeadObj(null)
      return
    }

    const user = allUsers[id]
    if (user) {
      setLeadObj(user)
    } else {
      loadUser(id)
    }
  }, [values, allUsers, loadUser])

  // Set owner
  React.useEffect(() => {
    const id = values.owner_id
    if (!id) {
      setOwnerObj(null)
      return
    }

    const user = allUsers[id]
    if (user) {
      setOwnerObj(user)
    } else {
      loadUser(id)
    }
  }, [values, allUsers, loadUser])

  return (
    <>
      <section className={classes.taskInfo}>
        <div className={clsx(classes.infoColumn1, classes.top)}>
          <ClientSection
            classes={classes}
            canEdit={isSubTask ? false : canEdit}
            canAddClient={canAddClient}
            searchUser={searchUser}
            setModifiedTask={setModifiedTask}
            clientObj={clientObj}
            setClientObj={setClientObj}
            sendValues={sendValues}
            newUserName={newUserName}
            setNewUserName={setNewUserName}
          />

          <CategorySection
            classes={classes}
            categories={categories}
            handleChange={handleChange}
            values={values}
            canEdit={canEdit}
          />

          <TypeSection
            classes={classes}
            types={applyTaskCategoryRules(values.section, types)}
            values={values}
            canEdit={canEdit}
            handleChange={handleChange}
          />

          <PrioritySection
            classes={classes}
            priorities={priorities}
            canEdit={canEdit}
            handleChange={handleChange}
            values={values}
          />

          <DueDateSection
            classes={classes}
            handleChange={handleChange}
            canEdit={canEdit}
            values={values}
          />
        </div>

        <div className={clsx(classes.infoColumn2, classes.top)}>
          <OwnerSection
            classes={classes}
            canEdit={isSubTask ? false : canEdit}
            setModifiedTask={setModifiedTask}
            sendValues={sendValues}
            ownerObj={ownerObj}
            setOwnerObj={setOwnerObj}
          />

          <PrimaryAssigneeSection
            classes={classes}
            canEdit={canEdit}
            setModifiedTask={setModifiedTask}
            sendValues={sendValues}
            leadObj={leadObj}
            setLeadObj={setLeadObj}
          />

          <AssignedToSection
            classes={classes}
            values={values}
            allUsers={allUsers}
            removeUser={removeUser}
            noPrimary={noPrimary}
            canEdit={canEdit}
            canSearchUser={canSearchUser}
            handleChange={handleChange}
          />

          <WatchersSection
            classes={classes}
            values={values}
            agents={agents}
            removeUser={removeUser}
            canEdit={canEdit}
            canSearchUser={canSearchUser}
            allUsers={allUsers}
            handleChange={handleChange}
          />

          <StatusSection
            classes={classes}
            statuses={statuses}
            values={values}
            handleChange={handleChange}
            canEdit={canEdit}
            canComplete={canComplete}
          />
        </div>
      </section>

      {showExpand && (
        <div className={classes.arrow} onClick={() => setExpanded(!expanded)}>
          <ExpansionArrow expanded={expanded} />{' '}
          {expanded ? 'SHOW LESS' : 'SHOW MORE'}
        </div>
      )}

      {expanded && (
        <section
          className={clsx(classes.taskInfo, classes.taskInfo2)}
          style={{ position: 'inherit', zIndex: '2' }}
        >
          <div className={clsx(classes.infoColumn1, classes.bottom)}>
            <TeamSection
              classes={classes}
              teams={teams}
              values={values}
              canEdit={canEdit}
              handleChange={handleChange}
              isCreating
            />
          </div>

          <div className={clsx(classes.infoColumn2, classes.bottom)}></div>
        </section>
      )}
    </>
  )
}

export default TaskDetails
